import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { EventType } from "../../../types/event";
import styles from "./RecurringSeriesBanner.module.css";

interface RecurringSeriesBannerProps {
	type: EventType;
	variant: "edit" | "delete";
	/**
	 * Only used for variant='delete' — interpolated into body copy via {{title}}.
	 * PHI gated to user-facing modal copy only (UI-SPEC §304-306;
	 * RESEARCH §Cross-cutting PHI invariant 4 — Sentry/PostHog payloads never see this).
	 */
	titleInterpolation?: string;
}

/**
 * Phase 37 D-19 — whole-series banner rendered when parseSyntheticEventId(id) !== null.
 *
 * Edit variant: static i18n key per type, no interpolation.
 *   Key: `event.edit.recurringBanner.{type}` — UI-SPEC §"Copywriting Contract" row
 *   "Whole-series banner — edit (per type)".
 *
 * Delete variant: interpolates {{title}} (user's own data into user's own modal —
 *   the ONLY permitted PHI surface in Phase 37 per RESEARCH §Cross-cutting PHI
 *   invariant 4; Sentry/PostHog redact `title` via phiScrub.ts).
 *   Key: `event.delete.body.recurring`.
 */
export function RecurringSeriesBanner({
	type,
	variant,
	titleInterpolation,
}: RecurringSeriesBannerProps) {
	const { t } = useClientTranslations("track");
	const text =
		variant === "edit"
			? t(`event.edit.recurringBanner.${type}`)
			: t("event.delete.body.recurring", { title: titleInterpolation ?? "" });

	return (
		<div className={styles.banner} role="status">
			<ExclamationTriangleIcon className={styles.icon} aria-hidden="true" />
			<p className={styles.text}>{text}</p>
		</div>
	);
}
